import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../interfaces/common.interfaces';
import {
  PaymentBalanceResponse,
  ClientInvoices,
  Agreement,
  PaymentByProducerRequest,
  PaymentResponse,
  AgreementQuotesRequest,
  AgreementQuotesResponse,
} from '../interfaces/payment.interfaces';
import { AuthService } from './auth.service';
import { map } from 'rxjs/operators';
@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private apiUrl = environment.int365_dom + '/api/v1';

  constructor(private http: HttpClient, private authService: AuthService) {}

  // Obtener balances de clientes
  getClientBalances(request: {
    producerNumber: number;
  }): Observable<ApiResponse<PaymentBalanceResponse[]>> {
    return this.http
      .get<ApiResponse<PaymentBalanceResponse[]>>(
        `${this.apiUrl}/PaymentBalance/clients`,
        this.getHttpOptions(request)
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error en la obtención de balances:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }

            throw error;
          }
        })
      );
  }

  // Obtener facturas del cliente
  getClientInvoices(body: {
    clientcode: number;
  }): Observable<ApiResponse<ClientInvoices>> {
    return this.http
      .get<ApiResponse<ClientInvoices>>(
        `${this.apiUrl}/PaymentBalance/invoices`,
        this.getHttpOptions(body)
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error en la obtención de facturas:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }

            throw error;
          }
        })
      );
  }

  // Obtener acuerdos del cliente
  getClientAgreements(body: {
    clientcode: number;
  }): Observable<ApiResponse<{ agreements: Agreement[] }>> {
    return this.http
      .get<ApiResponse<{ agreements: Agreement[] }>>(
        `${this.apiUrl}/PaymentBalance/agreement`,
        this.getHttpOptions(body)
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error en la obtención de acuerdos:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }

            throw error;
          }
        })
      );
  }

  // Procesar pago
  makePayment(
    request: PaymentByProducerRequest
  ): Observable<ApiResponse<PaymentResponse>> {
    return this.http
      .post<ApiResponse<PaymentResponse>>(
        `${this.apiUrl}/Payments/PayByProducer`,
        request,
        this.getHttpOptions()
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error al procesar el pago:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }

            throw error;
          }
        })
      );
  }

  // Autenticación
  private getHttpOptions(params?: any) {
    if (!this.authService.isAuthenticated()) {
      throw new Error('Usuario no autenticado');
    }

    const token = this.authService.getToken();
    if (!token) {
      throw new Error('No token found');
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return {
      headers,
      params: params
        ? new HttpParams({ fromObject: this.formatParams(params) })
        : undefined,
    };
  }

  private formatParams(params: any): any {
    const formattedParams: any = {};
    for (const key in params) {
      if (params[key] !== null && params[key] !== undefined) {
        if (params[key] instanceof Date) {
          formattedParams[key] = params[key].toISOString();
        } else {
          formattedParams[key] = params[key];
        }
      }
    }
    return formattedParams;
  }

  // Obtener cuotas de acuerdo
  getAgreementQuotes(
    request: AgreementQuotesRequest
  ): Observable<ApiResponse<AgreementQuotesResponse[]>> {
    return this.http
      .post<ApiResponse<AgreementQuotesResponse[]>>(
        `${this.apiUrl}/PaymentBalance/quota`,
        request,
        this.getHttpOptions()
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error al obtener cuotas de acuerdo:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }
            throw error;
          }
        })
      );
  }
}
